import type { CurrentInfo, Region } from '@protector/entities/region/models';
import { EventType } from '@protector/entities/timeline/constants';
import type {
  Annotation,
  Monitoring,
  Phenology,
  Sample,
  SprayWindow,
  TasksWindow,
  TimelineWindowEvents
} from '@protector/entities/timeline/models';
import type {
  DateISOString,
  DateOnlyString,
  Dictionary,
  Indicator,
  LatLngTuple,
  Moment,
  Page,
  Threshold,
  UUID
} from '@protector/utils/types';
import type { ISTMarkerProps, STTracking } from 'core/shared/map/map.models';
import type { mapboxgl } from 'core/utils/map/mapbox-gl';
import type { MouseEvent } from 'react';
import type { z } from 'zod';
import type { SortKeyFoundPhenomenons } from './area-day-info/components/area-day-info-events-monitoring/components/found-occurrences-by-categories/found-occurrences-by-categories.constants';
import type { MonitoringDetailThreshold } from './monitoring.models';
import type { getDaysWithoutMonitoringResponseSchema, getTimesMonitoredResponseSchema } from './schemas/days-without-monitoring-schema';
import type { getDaysWithoutSprayingResponseSchema } from './schemas/days-without-spraying-schema';
import type { getCategoryWithPhenomenaResponseSchema, phenomenonSchema } from './schemas/phenomena-with-category-schema';
import type { SprayInfo } from './spray.models';
import type { TrapWindowResponse } from './area-day-info/components/area-day-info-trap-info/trap-info.models';
import type { RainGaugeWindowResponse } from './area-day-info/components/area-day-info-rain-gauge-info/rain-gauge-info.models';

export { EventType, FILE_TYPE } from '@protector/entities/timeline/constants';
export type {
  Annotation,
  AnnotationStage,
  Area,
  CustomFields,
  Detail,
  Detasseling,
  DetasselingWindow,
  Emergence,
  EmergenceWindow,
  Flaming,
  FlamingWindow,
  Flowering,
  FloweringWindow,
  MaleDestruction,
  MaleDestructionWindow,
  Medias,
  Monitoring,
  MonitoringPoint,
  Phenology,
  PhenologySample,
  PhenologyStage,
  Planting,
  PlantingTask,
  PlantingWindow,
  Roguing,
  RoguingWindow,
  Sample,
  SprayWindow,
  TasksWindow,
  TimelineWindowEvents
} from '@protector/entities/timeline/models';

export type WindowEvent =
  | Monitoring
  | SprayWindow
  | Phenology
  | Annotation
  | TasksWindow
  | PlantingWindow
  | EmergenceWindow
  | FloweringWindow
  | DetasselingWindow
  | RoguingWindow
  | MaleDestructionWindow
  | FlamingWindow;

export interface TimelineWindowEventsFromBff extends Omit<TimelineWindowEvents, 'monitoring_window'> {
  monitoring_window?: MonitoringSorted;
}

export interface TimelineWindow {
  start_date: string;
  end_date: string;
  events: EventType[];
  phenological_stage: string;
  eventsData?: TimelineWindowEvents;
  startDate?: string;
  endDate?: string;
}

export interface TimelineBarPage {
  window: TimelineWindow[];
}

export enum TimelineMode {
  MAP = 'map',
  TABLE = 'table'
}

export type SeverityPointsValue = string | MonitoringDetailThreshold;

export interface TimelineAreaDayInfoProps {
  onClickNavigateBack: (region: Region) => void;
  currentInfo?: CurrentInfo;
  onClickPreviousNext: (previous: boolean) => void;
  area: Region;
  clearHeatmapsAndLayers: () => void;
  events?: TimelineWindowEvents | TimelineWindowEventsFromBff;
  trapWindowResponse?: TrapWindowResponse;
  rainGaugeWindowResponse?: RainGaugeWindowResponse;
  isLoading: boolean;
  loadingEvents: boolean;
  region: Region | null;
  handleEventClicked?: (clickedRegionId: UUID, event: EventType, startDate: string) => void;
  hasNextEvent?: boolean;
  hasPreviousEvent?: boolean;
  eventType: EventType;
  onDetailsClick: (ev: MouseEvent) => void;
  timeSpent?: number;
  scoutersDistance?: number;
  selectedArea: Region;
  trackings: STTracking[];
  handleSetTrackings: (trackings: STTracking[]) => void;
  handleToggleMarkers: () => void;
  showMarkers: boolean;
  mapMarkers: ISTMarkerProps[];
  removeLastSprayCurrentInfo: (spray: SprayInfo) => void;
  eventDates: Moment[];
  setLayout: (layout: LayoutTimeline) => void;
  setActivateSeverityPoints: (value: boolean) => void;
  setSeverityPoints: (value: Dictionary<Threshold> | undefined) => void;
  severityPoints: Dictionary<SeverityPointsValue> | undefined;
  activateSeverityPoints: boolean;
  loadingTrackings?: boolean;
  showTableLayout?: boolean;
  mapbox?: mapboxgl.Map;
  onSelectPhenologySample?: (sampleId?: string) => void;
  pages: ArrayOfPages<TimelineWindow>;
}

export interface LayoutTimeline {
  sideBar: {
    show: boolean;
    type: TimelineSideBarType;
    showAreaInfo?: boolean;
  };
  timelineBar: {
    show: boolean;
  };
  multilayer: {
    show: boolean;
  };
  table: {
    show: boolean;
  };
  legend: {
    show: boolean;
  };
}

export enum TimelineSideBarType {
  REGION_SELECT = 'REGION_SELECT',
  AREA_DAY_INFO = 'AREA_DAY_INFO'
}

export interface MonitoringSorted extends Omit<Monitoring, 'samples'> {
  samples: CategoriesSorted[];
}
export interface CategoriesSorted {
  id: string;
  name: string;
  description: string;
  display_priority: number;
  status?: string;
  phenomena: SampleSorted[];
}

export interface IndicatorShorted extends Indicator {
  display_priority: number;
  description: string;
  liss: number;
}

export interface SampleSorted extends Sample {
  display_priority: number;
}

interface ProductUsage {
  product_id?: string;
  name?: string;
  type?: string;
  dose?: number;
  dose_unit?: string;
  total?: number;
  activePrinciples?: string[];
}

export interface Spray {
  // scouter?: Scouter;
  assignee_id: string;
  date: DateISOString;
  // endDate?: DateISOString;
  // areas?: Area[];
  products_usages: ProductUsage[];
  // details?: SprayDetails;
}

export interface RequestTracking {
  query: {
    property: UUID;
    since: DateISOString;
    until: DateISOString;
    assignees: UUID[];
    regions: UUID[];
    geometry_date?: DateOnlyString;
  };
  selectedArea: Region;
}

export interface RequestTrackingExport {
  query: {
    property: UUID;
    since: string;
    until: string;
    assignees: UUID[];
    regions: UUID[];
    geometry_date?: DateOnlyString;
  };
}

export interface TrackingSegment {
  start_index: number;
  end_index: number;
  start: string;
  end: string;
  total_meters: number;
}

interface TrackingAssignee {
  id: string;
  name: string;
  route: LatLngTuple[];
  segments: TrackingSegment[];
  timestamps: string[];
}

export interface TrackingAssigneeResponse {
  assignees: TrackingAssignee[];
}

export const initialLayout = {
  sideBar: {
    show: true,
    type: TimelineSideBarType.REGION_SELECT
  },
  timelineBar: {
    show: false
  },
  multilayer: {
    show: true
  },
  table: {
    show: false
  },
  legend: {
    show: true
  }
};

export enum EventKeys {
  annotation_window = 'ANNOTATION',
  monitoring_window = 'MONITORING',
  phenology_window = 'PHENOLOGY',
  spray_window = 'APPLICATION',
  custom_forms_window = 'CUSTOM_FORM'
}

export const MAX_PAGES = 8;

export type ArrayOfPages<T> = Page<T>[];

export interface GetNotesMarkerIconProps {
  marker: ISTMarkerProps;
  hasPhoto: boolean;
  isSingleOrMultiplePhotos: boolean;
  enableMultiplePhotos: boolean;
  hasAudio: boolean;
}

export interface SaveEditAreaInfoResponse {
  seasonPropertyId: string;
  areaId: string;
  seasonId: string;
  cropId: string;
  varieties: string[];
  varietyName: string;
  plantingDate: string;
  emergencyDate: string;
  harvestingDate: string;
  areaInHectares: number;
  methodologyId: string;
  startsAt: string;
  endsAt: string;
  lastModified: string;
  deleted: boolean;
  id: string;
}

export interface IExtendAttrSpain {
  sigpacCode: string;
  dgcCode: string;
}

export const PLANTING_TASK_TRANSLATION_KEYS: Record<string, string> = {
  FEMALE: 'pages.timeline.planting.female',
  MALE: 'pages.timeline.planting.male',
  PRE_PLANTING: 'pages.timeline.planting.pre_planting',
  TRANSPLANTING: 'pages.timeline.planting.transplanting'
};

export interface ITimelineContext {
  regionId: UUID | null;
  setRegionId: (regionId: UUID | null) => void;
  timelineMode: TimelineMode;
}

export interface GetTimelineWindowsEventsProps {
  property_id: string;
  area_id: string;
  season_ids: string[];
  startEnd: { start: string; end: string };
  enableMultiplePhotosNotes: boolean;
  company_id: string;
  result_ordering?: SortKeyFoundPhenomenons;
  include_extra_types: string[];
  include_visualizations: boolean;
}

export type GetCategoryWithPhenomenaResponse = z.infer<typeof getCategoryWithPhenomenaResponseSchema>;

export type DrawerListPhenomenon = z.infer<typeof phenomenonSchema>;

export type GetDaysWithoutMonitoringResponse = z.infer<typeof getDaysWithoutMonitoringResponseSchema>;

export type GetTimesMonitoredResponse = z.infer<typeof getTimesMonitoredResponseSchema>;

export type GetDaysWithoutSprayingResponse = z.infer<typeof getDaysWithoutSprayingResponseSchema>;

export interface GetEventsParams {
  companyId?: string | null;
  propertyId: string | null;
  areaId: string | null;
  seasonIds: string[];
  flags: Dictionary<string | boolean> | null;
  ignoredEvents?: Set<EventType>;
}

export enum ShapefileDownloadError {
  INVALID_INPUT = 'INVALID_INPUT',
  NO_SHAPEFILES = 'NO_SHAPEFILES',
  FIELD_NOT_FOUND = 'FIELD_NOT_FOUND'
}

export type ShapefileDownloadResult = { kind: 'success'; signedLink: string } | { kind: 'error'; error: ShapefileDownloadError };
